import CSPricingListViewCell from 'components/configured-service/CreateDraftConfiguredServiceDialog/CSPricingListViewCell'
import T from 'T'
import { isFunction, isString, sortBy, get, capitalize } from 'lodash'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { getPricingPeriod } from 'utils/service'
import { getRecurrenceLabel } from 'components/pricing/pricingMethods'
import { getGridNumericOperators, getGridSingleSelectOperators, getGridStringOperators } from '@mui/x-data-grid'
import { useMemo } from 'react'
import { EVENT_INSTANCE_TYPES } from 'settings/constants/service'
import { ALL_DAYS, WEEK_OF_MONTH_IN_NUMBERS } from 'settings/constants/day'
import { useSelector } from 'react-redux'

const { SERVICE_NAME } = T
export const PRICED_SERVICE_TYPE = {
  RECURRING: 'RECURRING',
  ON_REQUEST: 'ON_REQUEST',
}
/**
 * Gets a property from an item using a getter which can be a function, string, or fallback to default properties
 * @param {Object} item - The item to get the property from
 * @param {string|Function} getter - The property name or function to get the value
 * @returns {*} - The retrieved value
 */
const getPropertyValue = (item, getter) => {
  if (isFunction(getter)) {
    return getter(item)
  }
  if (isString(getter)) {
    return item[getter]
  }
  return undefined
}

/**
 * Maps a list of objects to an array of objects with label and value properties
 * @param {Array} items - The array of objects to map
 * @param {string|Function} labelGetter - The property name or function to get the label
 * @param {string|Function} valueGetter - The property name or function to get the value
 * @returns {Array} - An array of objects with label and value properties
 */
export const mapToLabelValue = (items = [], labelGetter, valueGetter) => {
  return sortBy(
    items.map(item => {
      const label = getPropertyValue(item, labelGetter)
      const value = getPropertyValue(item, valueGetter)

      return { ...item, label, value }
    }),
    'label'
  )
}

export const defaultValues = {
  serviceStartDate: new Date(),
  quantity: 1,
  pricedServiceId: '',
  price: 0,
  eventInstanceType: EVENT_INSTANCE_TYPES.DAY_OF_WEEK,
  eventOccurrence: [WEEK_OF_MONTH_IN_NUMBERS[0].key],
  dayOfTheWeek: [ALL_DAYS[0]],
  dayOfTheMonth: ['1'],
}

export const useGetColumns = () => {
  const globalFilters = useSelector(state => get(state, 'GlobalSearchReducer.globalFilterOptions', null))
  const { businessLines, materials, measure, methods, pricingPeriod, pricingZones, recurrence, serviceAction } = globalFilters || {}
  const businessLineOptions = mapToLabelValue(businessLines, 'businessLineLabel', 'businessLineLabel')
  const materialOptions = mapToLabelValue(materials, 'materialType', 'materialType')
  const measureOptions = mapToLabelValue(
    measure,
    ({ unit = '', volume = 0 }) => `${volume} ${getMeasureUnit(unit)}`,
    ({ unit = '', volume = 0 }) => `${volume} ${getMeasureUnit(unit)}`
  )
  const methodOptions = mapToLabelValue(methods, 'methodType', 'methodType')
  const pricingPeriodOptions = mapToLabelValue(
    pricingPeriod,
    ({ periodInterval, periodPer }) => capitalize(getPricingPeriod(periodPer, periodInterval)),
    'methodType'
  )
  const pricingZoneOptions = mapToLabelValue(pricingZones, 'zoneName', 'zoneName')
  const recurrenceOptions = mapToLabelValue(recurrence, getRecurrenceLabel, getRecurrenceLabel)
  const serviceActionOptions = mapToLabelValue(serviceAction, 'actionType', 'actionType')

  // const taxZoneOptions = mapToLabelValue(taxZones, 'zoneName', 'zoneName')
  const isAnyOfSingleSelectOperators = getGridSingleSelectOperators().filter(operator => operator.value === 'isAnyOf')
  const containsStringOperator = getGridStringOperators().filter(operator => operator.value === 'contains')
  const isEqualNumberOperator = getGridNumericOperators().filter(operator => operator.value === '=')

  const columns = useMemo(
    () => [
      {
        field: 'id',
        flex: 1,
        minWidth: 0,
        renderCell: CSPricingListViewCell,
        getApplyQuickFilterFn: () => null,
      },
      {
        minWidth: 0,
        field: 'serviceName',
        filterable: true,
        type: 'string',
        headerName: SERVICE_NAME,
        filterOperators: containsStringOperator,
      },
      {
        minWidth: 0,
        field: 'serviceType',
        headerName: T.SERVICE_TYPE,
        type: 'singleSelect',
        filterable: true,
        valueOptions: [PRICED_SERVICE_TYPE.RECURRING, PRICED_SERVICE_TYPE.ON_REQUEST],
        getApplyQuickFilterFn: () => null,
      },
      {
        minWidth: 0,
        field: 'businessLine',
        headerName: T.BUSINESS_LINE,
        type: 'singleSelect',
        valueOptions: businessLineOptions,
        filterOperators: isAnyOfSingleSelectOperators,
        filterable: true,
        getApplyQuickFilterFn: () => null,
      },
      {
        minWidth: 0,
        field: 'materialType',
        headerName: T.MATERIAL,
        type: 'singleSelect',
        valueOptions: materialOptions,
        filterOperators: isAnyOfSingleSelectOperators,
        filterable: true,
        getApplyQuickFilterFn: () => null,
      },
      {
        minWidth: 0,
        field: 'measureText',
        headerName: T.MEASURE,
        type: 'singleSelect',
        valueOptions: measureOptions,
        filterOperators: isAnyOfSingleSelectOperators,
        filterable: true,
        getApplyQuickFilterFn: () => null,
      },
      {
        minWidth: 0,
        field: 'methodType',
        headerName: T.METHOD,
        type: 'singleSelect',
        valueOptions: methodOptions,
        filterOperators: isAnyOfSingleSelectOperators,
        filterable: true,
        getApplyQuickFilterFn: () => null,
      },
      {
        minWidth: 0,
        field: 'actionType',
        headerName: T.ACTION,
        type: 'singleSelect',
        valueOptions: serviceActionOptions,
        filterOperators: isAnyOfSingleSelectOperators,
        filterable: true,
        getApplyQuickFilterFn: () => null,
      },
      {
        minWidth: 0,
        field: 'zoneName',
        headerName: T.PRICING_ZONE,
        type: 'singleSelect',
        valueOptions: pricingZoneOptions,
        filterOperators: isAnyOfSingleSelectOperators,
        filterable: true,
        getApplyQuickFilterFn: () => null,
      },
      {
        minWidth: 0,
        field: 'recurrenceFrequency',
        headerName: T.RECURRENCE,
        type: 'number',
        filterOperators: isEqualNumberOperator,
        filterable: true,
        getApplyQuickFilterFn: () => null,
      },
      {
        minWidth: 0,
        field: 'pricingPeriod',
        headerName: T.PRICING_PERIOD_LABEL,
        type: 'singleSelect',
        valueOptions: pricingPeriodOptions,
        filterOperators: isAnyOfSingleSelectOperators,
        valueGetter: (value, row) => {
          return capitalize(getPricingPeriod(row.periodPer, row.periodInterval))
        },
        filterable: true,
        getApplyQuickFilterFn: () => null,
      },
      {
        minWidth: 0,
        field: 'recurrenceInterval',
        headerName: T.RECURRENCE_INTERVAL,
        type: 'singleSelect',
        filterOperators: isAnyOfSingleSelectOperators,
        valueGetter: (value, row) => {
          return getRecurrenceLabel(row)
        },
        valueOptions: recurrenceOptions,
        filterable: true,
        getApplyQuickFilterFn: () => null,
      },
    ],
    [
      businessLineOptions,
      materialOptions,
      measureOptions,
      methodOptions,
      pricingPeriodOptions,
      pricingZoneOptions,
      recurrenceOptions,
      serviceActionOptions,
      isAnyOfSingleSelectOperators,
      containsStringOperator,
      isEqualNumberOperator,
    ]
  )
  return columns
}
